import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSettings } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCookiePolicy } from '../services/dataService';
import RichTextRenderer from '../components/RichTextRenderer';

const CookiePolicyPage = () => {
  const [cookiePolicy, setCookiePolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCookiePolicy = async () => {
      try {
        const data = await getCookiePolicy();
        setCookiePolicy(data);
      } catch (error) {
        console.error('Failed to load cookie policy:', error);
        setCookiePolicy({ title: 'Cookie Policy', content: 'Cookie policy content not available at the moment.' });
      } finally {
        setLoading(false);
      }
    };

    loadCookiePolicy();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        {/* Header Section Skeleton */}
        <section className="bg-emerald-900 py-24 text-white relative text-center">
          <div className="max-w-4xl mx-auto px-4">
            <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-white/20 animate-pulse">
              <div className="w-10 h-10 bg-emerald-400/30 rounded"></div>
            </div>
            <div className="h-16 bg-white/10 rounded-lg mx-auto mb-6 max-w-2xl animate-pulse"></div>
            <div className="h-6 bg-emerald-100/20 rounded mx-auto max-w-xl animate-pulse"></div>
          </div>
        </section>

        {/* Content Section Skeleton */}
        <section className="py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="space-y-6">
                <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
                <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-4/5"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header Section */}
      <section className="bg-emerald-900 py-24 text-white relative text-center">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-white/20">
              <SafeIcon icon={FiSettings} className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-5xl lg:text-7xl font-black mb-6">Cookie <span className="text-emerald-400">Policy</span></h1>
            <p className="text-xl text-emerald-100/70">Learn about how we use cookies to improve your browsing experience.</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl p-8 lg:p-12"
          >
            {cookiePolicy && (
              <>
                <RichTextRenderer content={cookiePolicy.content} />
                {cookiePolicy.last_updated && (
                  <div className="mt-12 pt-8 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      Last updated: {new Date(cookiePolicy.last_updated).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;